import { useCallback, useRef, useState } from "react";
import * as pmRepo from "@/stores/pm/repository";
import {
  protocolDataKey,
  protocolQueryBounds,
  type ProtocolDateRange,
} from "@/domains/pm/utils/protocol";

export type ProtocolCountStatus = "idle" | "loading" | "ready" | "error";

export interface ProtocolCountState {
  status: ProtocolCountStatus;
  count: number;
  /** The cap was hit, so `count` is a floor — say "at least N", never "N". */
  truncated: boolean;
  /** Which period `count` belongs to. */
  key: string | null;
}

const IDLE: ProtocolCountState = { status: "idle", count: 0, truncated: false, key: null };

/**
 * How many issues a period holds, shown on step 1 before the user commits to it.
 *
 * Same shape as `useProtocolData` — imperative, abortable, cached for the life
 * of one open of the dialog — because it answers the same question a step
 * earlier and far more cheaply. Not React Query: this domain does not use it,
 * and the abort contract here is load-bearing.
 */
export function useProtocolCount(params: { buildingId: number; buildingProjectId?: number }) {
  const { buildingId, buildingProjectId } = params;
  const [state, setState] = useState<ProtocolCountState>(IDLE);
  const cacheRef = useRef(new Map<string, { count: number; truncated: boolean }>());
  const inFlightRef = useRef<{ key: string; controller: AbortController } | null>(null);

  const abort = useCallback(() => {
    inFlightRef.current?.controller.abort();
    inFlightRef.current = null;
  }, []);

  const count = useCallback(
    (range: ProtocolDateRange) => {
      const key = protocolDataKey(buildingId, range, buildingProjectId);

      const cached = cacheRef.current.get(key);
      if (cached) {
        setState({ status: "ready", ...cached, key });
        return;
      }
      if (inFlightRef.current?.key === key) return;
      // Clicking through presets must not leave an older, slower count to land
      // on top of the one the user is actually waiting for.
      inFlightRef.current?.controller.abort();

      const controller = new AbortController();
      inFlightRef.current = { key, controller };
      setState({ status: "loading", count: 0, truncated: false, key });

      void (async () => {
        try {
          const result = await pmRepo.countProtocolIssues(buildingId, {
            ...protocolQueryBounds(range),
            buildingProjectId,
            signal: controller.signal,
          });
          // Both guards matter: abort() does not un-resolve a settled promise,
          // so only the identity check catches an out-of-order pair.
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          cacheRef.current.set(key, result);
          inFlightRef.current = null;
          setState({ status: "ready", ...result, key });
        } catch (error) {
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          inFlightRef.current = null;
          // A count is a convenience, not the step's purpose — the failure is
          // silent in the UI and diagnosable only here.
          console.error("Protocol issue count failed", error);
          setState({ status: "error", count: 0, truncated: false, key });
        }
      })();
    },
    [buildingId, buildingProjectId]
  );

  /** Adopt a total the real fetch already paid for, rather than re-counting. */
  const seed = useCallback((key: string, total: number) => {
    cacheRef.current.set(key, { count: total, truncated: false });
  }, []);

  return { state, count, abort, seed };
}
